/**
 * Make a PARTIAL markdown string safe to render mid-stream so the view does
 * not flicker between "raw ** and ``` characters" and the formatted result as
 * tokens arrive. Only the render input is changed; the stored message text is
 * never mutated, and the final render (streaming=false) uses the exact text.
 *
 * - An unterminated fenced code block is closed (the code box grows in place
 *   instead of the whole tail reflowing when the closing fence arrives).
 * - In the last paragraph only: an odd number of `, ** or * is closed, and a
 *   dangling link `[label](http://par` collapses to its label.
 * Earlier paragraphs are left untouched so literal asterisks there are safe.
 */
const FENCE = /^ {0,3}(`{3,}|~{3,})/

export function balanceStreamingMarkdown(src: string): string {
  if (!src) return src
  const lines = src.split('\n')
  let open: string | null = null
  let lastBlank = -1
  for (let i = 0; i < lines.length; i += 1) {
    const m = FENCE.exec(lines[i])
    if (open) {
      if (m && m[1][0] === open[0] && m[1].length >= open.length && lines[i].trim() === m[1]) {
        open = null
      }
    } else if (m) {
      open = m[1]
    } else if (lines[i].trim() === '') {
      lastBlank = i
    }
  }
  if (open) {
    // Trim a half-typed closing fence ("``") before closing for real.
    const last = lines[lines.length - 1]
    if (last && /^ {0,3}[`~]{1,2}$/.test(last) && last[0] === open[0]) lines.pop()
    return `${lines.join('\n')}\n${open}`
  }
  const head = lines.slice(0, lastBlank + 1).join('\n')
  const tail = lines.slice(lastBlank + 1).join('\n')
  return head ? `${head}\n${balanceInline(tail)}` : balanceInline(tail)
}

function balanceInline(text: string): string {
  let out = text
  // Dangling links / images: show the label only until the URL completes.
  out = out.replace(/!?\[([^\]\n]*)\]\([^)\n]*$/, '$1')
  out = out.replace(/!?\[([^\]\n]*)$/, '$1')

  // Inline code: odd backticks => close it, and stop (nothing inside is markup).
  const ticks = (out.match(/`/g) ?? []).length
  if (ticks % 2 === 1) return `${out}\``

  // Strip code spans before counting emphasis markers.
  const bare = out.replace(/`[^`]*`/g, '')
  // Remove list-bullet asterisks at line start so they are not counted.
  const noBullets = bare.replace(/^\s*[*+-]\s/gm, '')
  const strong = (noBullets.match(/\*\*/g) ?? []).length
  const single = (noBullets.replace(/\*\*/g, '').match(/\*/g) ?? []).length
  // A lone trailing marker with no text yet ("**") would render as literal; hide it.
  if (/(^|\s)(\*\*|\*)$/.test(out)) return out.replace(/(\*\*|\*)$/, '')
  if (strong % 2 === 1) out += '**'
  if (single % 2 === 1) out += '*'
  return out
}
